// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link } from "@tanstack/react-router";
import { IdCard } from "lucide-react";
import { cn } from "@/lib/utils";

interface VendorLinkProps {
  /** Route slug — the vendor's name. When omitted (vendor couldn't be resolved), renders as plain text. */
  vendorName?: string;
  /** Display label — typically the vendor's name. */
  legibleName: string;
  className?: string;
  showIcon?: boolean;
}

/**
 * Linked vendor reference: leading vendor icon (matching the sidebar nav) + name, pointing to
 * `/iam/vendors/$vendorName`. Falls back to plain text when `vendorName` is missing (e.g. an
 * unresolved vendor id in the imports "Uploaded By" column).
 */
export function VendorLink({ vendorName, legibleName, className, showIcon = true }: VendorLinkProps) {
  if (!vendorName) {
    return (
      <span className={cn("inline-flex items-center gap-1.5", className)}>
        {showIcon && <IdCard className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />}
        <span>{legibleName}</span>
      </span>
    );
  }
  return (
    <Link
      to="/iam/vendors/$vendorName"
      params={{ vendorName }}
      className={cn("inline-flex items-center gap-1.5 text-primary hover:underline", className)}
    >
      {showIcon && <IdCard className="size-3.5 shrink-0" aria-hidden="true" />}
      <span>{legibleName}</span>
    </Link>
  );
}
